import {useCallback, useState} from 'react';
import {toast} from 'sonner';
import {FastAPIServiceError} from '@guestapp/sdk';
import {useFinishUserAccountMutation} from 'hooks/auth/useFinishUserAccountMutation';
import {useInitUserAccountMutation} from 'hooks/auth/useInitUserAccountMutation';
import {useAuth} from 'hooks/auth/useAuth';

type Props = {
  onSignUpSuccess?: () => void;
  onSignUpError?: (error: FastAPIServiceError) => void;
  onSignUpConfirmationSuccess?: () => void;
  onSignUpConfirmationError?: (error: FastAPIServiceError) => void;
};
export function useSignUp({
  onSignUpSuccess,
  onSignUpError,
  onSignUpConfirmationSuccess,
  onSignUpConfirmationError,
}: Props) {
  const [sessionId, setSessionId] = useState('');
  const {authenticate} = useAuth();

  const {mutate: signUp, isLoading: isSignUpLoading} = useInitUserAccountMutation({
    onSuccess: data => {
      setSessionId(data.session_id);
      onSignUpSuccess?.();
    },
    onError: error => {
      console.error(error);
      toast.error(
        'An error occurred during sign-up. Please try again or contact support.',
      );
      onSignUpError?.(error);
    },
  });

  const {mutate: finalizeUserAccountMutation, isLoading: isSignUpConfirmationLoading} =
    useFinishUserAccountMutation({
      onSuccess: data => {
        authenticate({token: data.access_token});
        onSignUpConfirmationSuccess?.();
      },
      onError: error => {
        console.error(error);
        toast.error(
          'An error occurred during sign-up confirmation. Please try again or contact support.',
        );
        onSignUpConfirmationError?.(error);
      },
    });

  const confirmSignUp = useCallback(
    ({code}: {code: string}) => {
      finalizeUserAccountMutation({
        code,
        session_id: sessionId,
      });
    },
    [finalizeUserAccountMutation, sessionId],
  );

  return {
    signUp,
    confirmSignUp,
    isLoading: isSignUpLoading || isSignUpConfirmationLoading,
  };
}
